import { ApplicationConfig, ErrorHandler, isDevMode, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withPreloading, withRouterConfig, PreloadAllModules } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { getApp } from 'firebase/app';
import { initializeFirestore, persistentLocalCache } from 'firebase/firestore';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { provideFirestore } from '@angular/fire/firestore';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { TranslocoHttpLoader } from './transloco-loader';
import { ErrorLoggingHandler } from './core/error-logging.handler';

function getInitialLang(): string {
  if (typeof localStorage === 'undefined') return 'en';
  const saved = localStorage.getItem('lang');
  return saved === 'mk' || saved === 'en' ? saved : 'en';
}

function preloadTranslocoFactory(transloco: TranslocoService): () => Promise<unknown> {
  return () =>
    firstValueFrom(
      transloco.load(getInitialLang()).pipe(catchError((err) => {
        console.warn('[Transloco] Preload failed, app will bootstrap without translations.', err);
        return of(null);
      }))
    ).catch(() => undefined);
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: ErrorLoggingHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    // 'computed' keeps the history stack in sync when the unsaved-changes guard
    // cancels a browser-back navigation.
    provideRouter(
      routes,
      withPreloading(PreloadAllModules),
      withRouterConfig({ canceledNavigationResolution: 'computed' })
    ),
    provideHttpClient(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideFirestore(() =>
      initializeFirestore(getApp(), { localCache: persistentLocalCache({}) })
    ),
    provideFunctions(() => getFunctions()),
    provideTransloco({
      config: {
        availableLangs: ['en', 'mk'],
        defaultLang: getInitialLang(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: preloadTranslocoFactory,
      deps: [TranslocoService],
    },
  ],
};
